/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Author } from '../../types';
import { colors, borderRadius, shadows, typography } from '../../styles/theme';

interface AuthorCardProps {
  author: Author;
  variant?: 'default' | 'compact';
}

const cardStyles = css`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: ${colors.background.primary};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
`;

const compactCardStyles = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const avatarStyles = css`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: ${borderRadius.full};
  object-fit: cover;
  border: 3px solid ${colors.primary[100]};
`;

const compactAvatarStyles = css`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.full};
  object-fit: cover;
`;

const infoStyles = css`
  flex: 1;
`;

const nameStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin-bottom: 0.25rem;
`;

const compactNameStyles = css`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
`;

const roleStyles = css`
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary[700]};
  font-weight: ${typography.fontWeight.medium};
  margin-bottom: 0.5rem;
`;

const compactRoleStyles = css`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.muted};
`;

const bioStyles = css`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

export default function AuthorCard({ author, variant = 'default' }: AuthorCardProps) {
  if (variant === 'compact') {
    return (
      <div css={compactCardStyles}>
        <img css={compactAvatarStyles} src={author.avatar} alt={author.name} />
        <div>
          <div css={compactNameStyles}>{author.name}</div>
          <div css={compactRoleStyles}>{author.role}</div>
        </div>
      </div>
    );
  }

  return (
    <div css={cardStyles}>
      <img css={avatarStyles} src={author.avatar} alt={author.name} />
      <div css={infoStyles}>
        <h4 css={nameStyles}>{author.name}</h4>
        <div css={roleStyles}>{author.role}</div>
        {author.bio && <p css={bioStyles}>{author.bio}</p>}
      </div>
    </div>
  );
}
